/**
 * Aircraft Card Store - State machine for the Expanding Card UI
 *
 * Manages the card display state (compact, expanded, moveBar, fullModal),
 * edit form visibility, and user preferences (modal vs side panel).
 *
 * Kept separate from aircraftEditorStore (which manages drag/rotation/dirty tracking)
 * because card display state is orthogonal to editing state.
 * The two stores are bridged by the useAircraftCardSync hook.
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// ============================================
// Types
// ============================================

export type TabId = 'aircraft' | 'financial' | 'client' | 'schedule' | 'fuel'

export type CardState =
  | { mode: 'closed' }
  | { mode: 'compact' }
  | { mode: 'expanded'; activeTab: TabId }
  | { mode: 'moveBar' }
  | { mode: 'fullModal'; activeTab: TabId }

export type EditFormMode = 'modal' | 'sidePanel'
export type EnlargedViewMode = 'modal' | 'sidebar'

interface AircraftCardState {
  cardState: CardState
  editFormOpen: { formId: string; prefocusField?: string } | null
  editFormMode: EditFormMode
  enlargedViewMode: EnlargedViewMode
}

interface AircraftCardActions {
  openCompact: () => void
  toggleTab: (tabId: TabId) => void
  enterMoveBar: () => void
  exitMoveBar: () => void
  openFullModal: (tabId?: TabId) => void
  closeFullModal: () => void
  closeCard: () => void
  openEditForm: (formId: string, prefocusField?: string) => void
  closeEditForm: () => void
  setEditFormMode: (mode: EditFormMode) => void
  setEnlargedViewMode: (mode: EnlargedViewMode) => void
  toggleEnlargedViewMode: () => void
}

// ============================================
// Store
// ============================================

const initialState: AircraftCardState = {
  cardState: { mode: 'closed' },
  editFormOpen: null,
  editFormMode: 'modal',
  enlargedViewMode: 'modal',
}

export const useAircraftCardStore = create<AircraftCardState & AircraftCardActions>()(
  devtools(
    persist(
      (set, _get) => ({
        ...initialState,

        openCompact: () =>
          set({ cardState: { mode: 'compact' } }),

        toggleTab: (tabId) =>
          set((state) => {
            const { cardState } = state
            // If already expanded to this tab, collapse to compact
            if (cardState.mode === 'expanded' && cardState.activeTab === tabId) {
              return { cardState: { mode: 'compact' } }
            }
            // Otherwise expand to the requested tab
            return { cardState: { mode: 'expanded', activeTab: tabId } }
          }),

        enterMoveBar: () =>
          set({ cardState: { mode: 'moveBar' }, editFormOpen: null }),

        exitMoveBar: () =>
          set({ cardState: { mode: 'compact' } }),

        openFullModal: (tabId) =>
          set((state) => {
            const currentTab =
              state.cardState.mode === 'expanded'
                ? state.cardState.activeTab
                : 'aircraft'
            return {
              cardState: { mode: 'fullModal', activeTab: tabId ?? currentTab },
            }
          }),

        closeFullModal: () =>
          set({ cardState: { mode: 'compact' }, editFormOpen: null }),

        closeCard: () =>
          set({ cardState: { mode: 'closed' }, editFormOpen: null }),

        openEditForm: (formId, prefocusField) =>
          set((state) => ({
            editFormOpen: { formId, prefocusField },
            // Close full modal when opening edit form to prevent stacking
            cardState: state.cardState.mode === 'fullModal'
              ? { mode: 'compact' }
              : state.cardState,
          })),

        closeEditForm: () =>
          set({ editFormOpen: null }),

        setEditFormMode: (mode) =>
          set({ editFormMode: mode }),

        setEnlargedViewMode: (mode) =>
          set({ enlargedViewMode: mode }),

        toggleEnlargedViewMode: () =>
          set((state) => ({
            enlargedViewMode: state.enlargedViewMode === 'modal' ? 'sidebar' : 'modal',
          })),
      }),
      {
        name: 'planmax-aircraft-card',
        // Only persist user preferences, not transient UI state
        partialize: (state) => ({
          editFormMode: state.editFormMode,
          enlargedViewMode: state.enlargedViewMode,
        }),
      }
    ),
    { name: 'aircraft-card-store' }
  )
)
